// @mui
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
// components
import Iconify from 'src/components/iconify';
//
import { PRIVACY_POLICY_CONTENT as c } from './content';
import { PrivacyPolicyModalProps } from './types';

// ----------------------------------------------------------------------

export default function PrivacyPolicyModal({ open, onClose }: PrivacyPolicyModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(22, 28, 36, 0.2)',
            backdropFilter: 'blur(1px)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="span">{c.title}</Typography>

        <IconButton onClick={onClose} size="small">
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              {c.lastUpdated}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {c.intro}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1">{c.section1Title}</Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {c.section1ProvidedHeading}
            </Typography>
            <Typography variant="body2">{c.section1ProvidedIntro}</Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <Typography component="li" variant="body2">
                <Typography component="span" variant="subtitle2">
                  {c.section1AccountDataLabel}
                </Typography>{' '}
                {c.section1AccountDataText}
              </Typography>
              <Typography component="li" variant="body2">
                <Typography component="span" variant="subtitle2">
                  {c.section1ConnectionCredentialsLabel}
                </Typography>{' '}
                {c.section1ConnectionCredentialsText}
              </Typography>
            </Box>
            <Typography variant="body2" fontStyle="italic" sx={{ mt: 1 }}>
              {c.section1NoSensitive}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {c.section1AutomaticHeading}
            </Typography>
            <Typography variant="body2">{c.section1AutomaticIntro}</Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <Typography component="li" variant="body2">
                <Typography component="span" variant="subtitle2">
                  {c.section1LogDataLabel}
                </Typography>{' '}
                {c.section1LogDataText}
              </Typography>
              <Typography component="li" variant="body2">
                <Typography component="span" variant="subtitle2">
                  {c.section1CookiesLabel}
                </Typography>{' '}
                {c.section1CookiesText}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1">{c.section2Title}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.section2Intro}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {c.heading}
            </Typography>
            <Typography variant="body2">
              <Typography component="span" variant="subtitle2">
                {c.dataCollectedLabel}
              </Typography>{' '}
              {c.dataCollectedText}
            </Typography>

            <Typography variant="body2">
              <Typography component="span" variant="subtitle2">
                {c.purposeLabel}
              </Typography>{' '}
              {c.purposeText}
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.legalGroundsLabel}
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <Typography component="li" variant="body2">
                {c.legalGroundsEu}
              </Typography>
              <Typography component="li" variant="body2">
                {c.legalGroundsFrance}
              </Typography>
              <Typography component="li" variant="body2">
                {c.legalGroundsLatam}
              </Typography>
              <Typography component="li" variant="body2">
                {c.legalGroundsUs}
              </Typography>
              <Typography component="li" variant="body2">
                {c.legalGroundsIsrael}
              </Typography>
              <Typography component="li" variant="body2">
                {c.legalGroundsJapan}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1">{c.section3Title}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.dataRetentionText}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1">{c.section4Title}</Typography>
            <Box component="ul" sx={{ pl: 3, m: 0, mt: 1 }}>
              <Typography component="li" variant="body2">
                <Typography component="span" variant="subtitle2">
                  {c.dataHostingLabel}
                </Typography>{' '}
                {c.dataHostingText}
              </Typography>
              <Typography component="li" variant="body2">
                <Typography component="span" variant="subtitle2">
                  {c.thirdPartyLabel}
                </Typography>{' '}
                {c.thirdPartyText}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1">{c.section5Title}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.section5Text}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1">{c.section6Title}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.section6Intro}
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <Typography component="li" variant="body2">
                {c.section6Right1}
              </Typography>
              <Typography component="li" variant="body2">
                {c.section6Right2}
              </Typography>
              <Typography component="li" variant="body2">
                {c.section6Right3}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.section6Outro}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1">{c.section7Title}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.section7Text}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
